/**
 * 사용자 랭킹 관련 서비스
 */

import { apiGet } from '../utils/apiClient';
import { getBadgeByRank } from '../utils/rankingUtils';

export interface UserRankData {
  rank: number;
  userKey: number;
  nickname: string;
  balance: number;
  gameCount: number;
}

export interface UserRankWithBadge extends UserRankData {
  badge: {
    id: string;
    name: string;
    icon: string;
    color: string;
  } | null;
}

/**
 * 현재 사용자의 랭킹 조회
 */
export async function fetchMyRank(
  apiBaseUrl: string
): Promise<UserRankWithBadge | null> {
  try {
    // email을 URL에 싣지 않고, JWT 기반으로 본인 랭킹 조회
    const data = await apiGet<any>(`${apiBaseUrl}/api/user/my-rank`);

    // apiGet은 보통 ResponseInterceptor를 거친 data를 반환하지만,
    // 혹시 { success, data } 형태로 올 수 있으므로 방어적으로 처리
    const actualData =
      data && typeof data === 'object' && 'success' in data ? data : { data };
    if ((actualData as any)?.success === false) {
      return null;
    }

    const rankData: UserRankData = (actualData as any)?.data || (data as any);
    const badge = getBadgeByRank(rankData.rank);

    console.log('🏅 사용자 배지 계산:', {
      rank: rankData.rank,
      badge: badge?.name || 'null',
      icon: badge?.icon || 'null',
    });

    return {
      ...rankData,
      badge: badge
        ? {
            id: badge.id,
            name: badge.name,
            icon: rankData.rank.toString(), // 단순히 숫자만 전달
            color: badge.color,
          }
        : null,
    };
  } catch (error) {
    console.error('❌ 사용자 랭킹 조회 중 오류:', error);
    return null;
  }
}

/**
 * 사용자 랭킹 캐시 (5분)
 */
let userRankCache: {
  data: UserRankWithBadge | null;
  timestamp: number;
} | null = null;

const USER_RANK_CACHE_DURATION = 5 * 60 * 1000; // 5분

/**
 * 캐시된 사용자 랭킹 조회
 */
export async function getCachedMyRank(
  apiBaseUrl: string,
  forceRefresh = false
): Promise<UserRankWithBadge | null> {
  // 캐시 확인
  if (
    !forceRefresh &&
    userRankCache &&
    Date.now() - userRankCache.timestamp < USER_RANK_CACHE_DURATION &&
    userRankCache.data !== null // null 값은 캐시하지 않음
  ) {
    console.log('✅ 사용자 랭킹 캐시 HIT');
    return userRankCache.data;
  }

  // 새로운 데이터 조회
  console.log('🔍 사용자 랭킹 조회 - 캐시 비활성화');
  const rankData = await fetchMyRank(apiBaseUrl);

  // 성공한 데이터만 캐시 저장
  if (rankData !== null) {
    userRankCache = {
      data: rankData,
      timestamp: Date.now(),
    };
    console.log('💾 사용자 랭킹 캐시 저장 (1분간 유효)');
  } else {
    console.log('❌ 랭킹 데이터가 null이므로 캐시하지 않음');
  }

  return rankData;
}

/**
 * 사용자 랭킹 캐시 무효화 (잔고 변경 시 호출)
 */
export function invalidateUserRankCache(): void {
  userRankCache = null;
  console.log('🗑️ 사용자 랭킹 캐시 무효화');
}
